import { formatDate } from "@angular/common";
import { Component, DestroyRef, DOCUMENT, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationError, Router, RouterOutlet } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { EventMessage, EventType as MsalEventType } from "@azure/msal-browser";
import { TranslateService } from "@ngx-translate/core";
import { NgHttpLoaderComponent } from "ng-http-loader";
import { filter, startWith } from "rxjs";
import { environment } from "src/environments/environment";
import { buildDate } from "./core/consts/build-date";
import { e2eTokenStorageKey } from "./core/consts/storage-keys";
import { EnvironmentId } from "./core/enums/environment-id.enum";
import { SpinnerLargeComponent } from "./shared/components/spinner-large/spinner-large.component";

@Component({
  selector: "app-root",
  imports: [RouterOutlet, NgHttpLoaderComponent],
  templateUrl: "./app.html",
  styleUrl: "./app.scss",
  host: {
    /* eslint-disable @typescript-eslint/naming-convention */
    "(window:pageshow)": "onWindowPageShow($event)",
    /* eslint-enable @typescript-eslint/naming-convention */
  },
})
export class App implements OnInit {
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly translate = inject(TranslateService);
  protected readonly document = inject(DOCUMENT, { optional: true }) ?? window.document;
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  protected readonly msal = inject(MsalService);
  protected readonly msalBroadcast = inject(MsalBroadcastService);

  /** Api urls that don't trigger the loading overlay */
  private readonly _filteredUrlPatterns: readonly string[] = [];

  public readonly filteredUrlPatterns = Array.from(this._filteredUrlPatterns);

  /** spinner used by api loading overlay */
  public readonly spinner = SpinnerLargeComponent;

  public ngOnInit(): void {
    console.log("Environment ➡", environment.id, formatDate(buildDate, "yyyyMMddHHmmssSSS", "en-US"));

    this.translate.onLangChange.pipe(startWith(null), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.document.documentElement.lang = this.translate.getCurrentLang();
    });

    // Ensures that there's something in the main router outlet after NavigationError
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (navigationError) => {
        if (!this.route.root.firstChild && navigationError.url && navigationError.url !== "/") {
          await this.router.navigate(["/"]);
        }
      });

    this.msalBroadcast.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === MsalEventType.INITIALIZE_END ||
            msg.eventType === MsalEventType.LOGIN_SUCCESS ||
            msg.eventType === MsalEventType.LOGOUT_END ||
            msg.eventType === MsalEventType.ACCOUNT_ADDED ||
            msg.eventType === MsalEventType.ACCOUNT_REMOVED
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (environment.id !== EnvironmentId.Production && sessionStorage.getItem(e2eTokenStorageKey)) {
          return;
        }
        const allAccounts = this.msal.instance.getAllAccounts() ?? [];
        const previousActive = this.msal.instance.getActiveAccount();
        if (previousActive && !allAccounts.includes(previousActive)) {
          this.msal.instance.setActiveAccount(null);
        }
        if (!this.msal.instance.getActiveAccount() && allAccounts.length > 0) {
          this.msal.instance.setActiveAccount(allAccounts[0]);
        }
      });
  }

  public onWindowPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.document.body.dataset["preventWindowUnload"] = "false";
      window.location.reload();
    }
  }
}
